import React, { Component } from "react";
import { Link } from "react-router-dom";
import { AxiosResponse, AxiosError } from "axios";
import { putRequest } from "../../utils/httpRequest";

interface Props {
  match: any;
}

type ResetState = "set" | "success" | "expired";

interface State {
  password: string;
  message: string;
  errorMessage: string;
  saving: boolean;
  passwordError: string;
  showPassword: boolean;
  showHelp: boolean;
  resetState: ResetState;
}

class ResetPassword extends Component<Props, State> {
  helpWrapRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      password: "",
      errorMessage: "",
      message: "",
      saving: false,
      passwordError: "",
      showPassword: false,
      showHelp: false,
      resetState: "set",
    };
  }

  componentDidMount() {
    document.body.classList.add("auth-login-page-active");
    document.addEventListener("mousedown", this.handleOutsideHelpClick);
  }

  componentWillUnmount() {
    document.body.classList.remove("auth-login-page-active");
    document.removeEventListener("mousedown", this.handleOutsideHelpClick);
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      password: e.target.value,
      errorMessage: "",
      passwordError: "",
    });
  };

  toggleShowPassword = () => {
    this.setState((prevState) => ({
      showPassword: !prevState.showPassword,
    }));
  };

  toggleHelp = () => {
    this.setState(({ showHelp }) => ({ showHelp: !showHelp }));
  };

  handleOutsideHelpClick = (e: MouseEvent) => {
    const helpWrap = this.helpWrapRef.current;

    if (!this.state.showHelp || !helpWrap) {
      return;
    }

    if (!helpWrap.contains(e.target as Node)) {
      this.setState({ showHelp: false });
    }
  };

  onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    this.setState({
      errorMessage: "",
      passwordError: "",
    });

    const id = this.props.match.params.id;
    const password = this.state.password.trim();

    if (!password) {
      this.setState({ passwordError: "Please enter your new password." });

      return;
    }

    if (password.length < 8) {
      this.setState({
        passwordError: "Password needs at least 8 characters.",
      });

      return;
    }

    const data = {
      session: {
        password,
        password_confirmation: password,
      },
    };

    this.setState({ saving: true });

    putRequest(`/session/password_resets/${id}`, data)
      .then((response: AxiosResponse) => {
        if (response.data.status === "token_expired") {
          this.setState({
            saving: false,
            resetState: "expired",
            message: "",
            password: "",
          });

          return;
        }

        if (response.data.status === "success") {
          this.setState({
            saving: false,
            resetState: "success",
            message: "Password was successfully updated.",
            password: "",
          });

          return;
        }

        this.setState({
          saving: false,
          errorMessage: "Sorry, we could not update your password. Try again.",
        });
      })
      .catch((error: AxiosError<{ error: string }>) => {
        this.setState({
          saving: false,
          errorMessage:
            error?.response?.data?.error ||
            "Sorry, we could not update your password. Try again.",
        });
        console.log(error);
      });
  };

  onCloseErrorMessage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    this.setState({ errorMessage: "" });
  };

  renderEyeIcon = () => {
    if (this.state.showPassword) {
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      );
    }

    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  };

  render() {
    const {
      password,
      message,
      errorMessage,
      passwordError,
      showPassword,
      showHelp,
      saving,
      resetState,
    } = this.state;

    return (
      <div className="auth-page" id="reset-password">
        <div className="auth-blob one" aria-hidden="true">
          <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#2E2437"
              d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
            />
            <path
              fill="#2E2437"
              d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
            />
          </svg>
        </div>
        <div className="auth-blob two" aria-hidden="true">
          <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#2E2437"
              d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
            />
            <path
              fill="#2E2437"
              d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
            />
          </svg>
        </div>

        <header className="auth-header">
          <a className="brand" href="/" aria-label="42courses home">
            <img
              src={require("../../../../assets/images/landings/home-logo.svg")}
              alt="42courses"
            />
          </a>
          <div className="help-wrap" ref={this.helpWrapRef}>
            <button
              type="button"
              className="help-link"
              aria-expanded={showHelp}
              aria-controls="reset-password-help-pop"
              onClick={this.toggleHelp}
            >
              Need a hand?
            </button>
            <div
              className="help-pop"
              id="reset-password-help-pop"
              role="dialog"
              aria-label="Need a hand?"
              hidden={!showHelp}
            >
              <p>
                If you are stuck or need a hand, reach out to us at{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>
                .
              </p>
            </div>
          </div>
        </header>

        <main className="auth-main">
          <section
            className="signin-card reset-card"
            id="reset-card"
            data-state={resetState}
            aria-labelledby="reset-password-title"
          >
            {saving && (
              <div className="status-banner" aria-live="polite">
                <span className="spinner" aria-hidden="true"></span>
                <span>Saving...</span>
              </div>
            )}

            {resetState === "set" && (
              <>
                <div className="signin-head">
                  <p className="signin-greeting">Almost done</p>
                  <h1 className="signin-title" id="reset-password-title">
                    Set a new password.
                  </h1>
                  <p className="signin-sub">
                    Pick something you will remember.
                  </p>
                </div>

                {errorMessage && (
                  <p className="status-banner error" role="alert">
                    <span>{errorMessage}</span>
                    <button
                      type="button"
                      className="status-close"
                      aria-label="Dismiss error"
                      onClick={this.onCloseErrorMessage}
                    >
                      &times;
                    </button>
                  </p>
                )}

                <form className="ui form reset-form" onSubmit={this.onSubmit}>
                  <div className="field reset-password-field">
                    <div className="ui icon action input">
                      <label htmlFor="reset_pwd" className="sr-only">
                        New password
                      </label>
                      <input
                        type={showPassword ? "text" : "password"}
                        id="reset_pwd"
                        name="password"
                        placeholder="New password (8+ characters)"
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={this.handleChange}
                        aria-invalid={Boolean(passwordError)}
                        aria-describedby={
                          passwordError
                            ? "reset_pwd_hint reset_pwd_error"
                            : "reset_pwd_hint"
                        }
                      />
                      <button
                        type="button"
                        className="pwd-toggle"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={this.toggleShowPassword}
                      >
                        {this.renderEyeIcon()}
                      </button>
                    </div>
                  </div>
                  <p className="field-hint" id="reset_pwd_hint">
                    Eight characters or more. A short phrase you will recognise
                    beats a tangle of symbols you will not.
                  </p>
                  {passwordError && (
                    <div
                      className="field-validation"
                      id="reset_pwd_error"
                      role="alert"
                    >
                      {passwordError}
                    </div>
                  )}
                  <div className="action">
                    <button
                      type="submit"
                      className={`ui button ${saving ? "loading" : ""}`}
                      disabled={saving}
                    >
                      Save new password
                    </button>
                  </div>
                </form>
              </>
            )}

            {resetState === "success" && (
              <>
                <div className="signin-head">
                  <div className="result-icon good" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  </div>
                  <p className="signin-greeting">All done</p>
                  <h1 className="signin-title" id="reset-password-title">
                    Password updated.
                  </h1>
                  <p className="signin-sub">
                    "You are all set. Sign in with your new password whenever
                    you are ready."
                  </p>
                </div>
                <Link to="/login" className="btn-auth btn-auth-blue">
                  Sign in
                </Link>
              </>
            )}

            {resetState === "expired" && (
              <>
                <div className="signin-head">
                  <div className="result-icon warn" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5" />
                      <path d="M12 16h.01" />
                    </svg>
                  </div>
                  <p className="signin-greeting">Hmm</p>
                  <h1 className="signin-title" id="reset-password-title">
                    This link has expired.
                  </h1>
                  <p className="signin-sub">
                    Reset links only last a while, for safety. Pop your email in
                    again and we will send a fresh one.
                  </p>
                </div>
                <a
                  href="/session/password_resets/new"
                  className="btn-auth btn-auth-outline"
                >
                  Request a new link
                </a>
              </>
            )}

            <p className="signin-foot">
              <a href="/terms">Terms</a> &nbsp;&middot;&nbsp;{" "}
              <a href="/privacy">Privacy</a>
            </p>
          </section>
        </main>

        <footer className="auth-footer">
          Made with care in London. &nbsp;&middot;&nbsp;{" "}
          <a href="/">Back to 42courses</a>
        </footer>
      </div>
    );
  }
}

export default ResetPassword;
